// MODALS (Andrei, 2026-10-27): "Add a priority" from the chart's seat panel — its Seat setup
// list and its Priorities section — opens this dialog over the chart instead of a form inside
// the panel, so the panel keeps its place. One priority (`goal.kind = priority`) for the seat
// and the quarter shown, with optional milestones, one per line. SEAT-DRAWER-MODALS: the
// Priorities modal edits one with the same dialog (`current`): its title and milestones, a
// milestone kept by its title keeping its id, done mark and due date.
import {
  cn,
  Field,
  FieldDescription,
  FieldLabel,
  Input,
  LensBandDot,
  QuickAddDialog,
  Textarea,
  toast,
} from "@companyos/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CheckIcon, CircleIcon } from "lucide-react";
import { useId, useState } from "react";
import type { Api } from "../../api/client";
import type { Goal } from "../../api/types";
import { FormError } from "../structure/parts";
import { chartKeys } from "./chart-queries";
import { TRACK_BAND, TRACK_LABELS } from "./keystones";
import { SectionModal } from "./section-modal";

const linesOf = (text: string) =>
  text
    .split("\n")
    .map((t) => t.trim())
    .filter(Boolean);

type Milestones = readonly Pick<Goal["milestones"][number], "id" | "title">[];

/**
 * The milestone set to write after an edit of the lines. The edit is read against the set the
 * dialog opened with (`baseline`): a line still there keeps that milestone (by id), a line gone
 * removes it, a new line adds one. It is then applied to the set as stored now (`fresh`): a kept
 * milestone is sent by id with its stored title and no done mark or due date (the server keeps
 * them), one deleted elsewhere is left out, and one added elsewhere stays.
 */
export function editedMilestones(text: string, baseline: Milestones, fresh: Milestones) {
  const left = [...baseline];
  const lines = linesOf(text).map((title) => {
    const i = left.findIndex((m) => m.title === title);
    return i < 0 ? { title } : { id: (left.splice(i, 1)[0] as Milestones[number]).id };
  });
  const now = new Map(fresh.map((m) => [m.id, m]));
  const known = new Set(baseline.map((m) => m.id));
  const out: { id?: string; title: string }[] = [];
  for (const l of lines) {
    if (!("id" in l)) out.push({ title: l.title });
    else {
      const m = now.get(l.id);
      if (m) out.push({ id: m.id, title: m.title });
    }
  }
  for (const m of fresh) if (!known.has(m.id)) out.push({ id: m.id, title: m.title });
  return out;
}

export function PriorityDialog({
  api,
  co,
  seatId,
  seatTitle,
  quarter,
  current,
  onClose,
}: {
  api: Api;
  co: string;
  seatId: string;
  seatTitle: string;
  /** The quarter the priority is for (`2026-Q4`); null leaves it to the server (the current). */
  quarter: string | null;
  /** Editing this priority; absent = a new one. */
  current?: Goal;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const id = useId();
  const [title, setTitle] = useState(current?.title ?? "");
  const [milestones, setMilestones] = useState(
    current ? current.milestones.map((m) => m.title).join("\n") : "",
  );
  // An edit sends the milestones only when their lines changed, so a title edit never writes back
  // done marks someone ticked while the dialog was open.
  const milestonesChanged =
    current !== undefined &&
    linesOf(milestones).join("\n") !== current.milestones.map((m) => m.title).join("\n");
  /** The edit applied to the goal as stored now (re-read, every page); gone or moved: refused. */
  const freshMilestones = async (g: Goal) => {
    let cursor: string | undefined;
    for (let i = 0; i < 20; i++) {
      const page = await api.client.goals.list({
        params: { co, id: seatId },
        query: {
          kind: "priority",
          ...(g.period_key ? { period_key: g.period_key } : {}),
          limit: 200,
          ...(cursor ? { cursor } : {}),
        },
      });
      const fresh = page.data.find((x) => x.id === g.id);
      if (fresh) return editedMilestones(milestones, g.milestones, fresh.milestones);
      if (!page.next_cursor) break;
      cursor = page.next_cursor;
    }
    throw new Error("This priority changed elsewhere (removed or moved). Close and open it again.");
  };
  const save = useMutation({
    mutationFn: async () =>
      current
        ? api.client.goals.update({
            params: { co, id: current.id },
            body: {
              ...(title.trim() !== current.title ? { title: title.trim() } : {}),
              ...(milestonesChanged ? { milestones: await freshMilestones(current) } : {}),
            },
          })
        : api.client.goals.create({
            params: { co, id: seatId },
            body: {
              title: title.trim(),
              ...(quarter ? { period_key: quarter } : {}),
              milestones: linesOf(milestones).map((t) => ({ title: t })),
            },
          }),
    onSuccess: () => {
      // The chart's cards and panel (goals sit under the chart's key) and KPIs › Priorities.
      void qc.invalidateQueries({ queryKey: chartKeys.orgChart(co) });
      void qc.invalidateQueries({ queryKey: ["company", co, "priorities"] });
      toast.add({ title: current ? "Priority saved" : "Priority added", type: "success" });
      onClose();
    },
  });
  return (
    <QuickAddDialog
      title={current ? "Edit priority" : "New priority"}
      info="What this seat delivers this quarter. Milestones are the steps you tick off on the way."
      description={quarter ? `${seatTitle} · ${quarter}` : seatTitle}
      submitLabel={current ? "Save priority" : "Add priority"}
      canSubmit={title.trim() !== ""}
      pending={save.isPending}
      onSubmit={() => save.mutate()}
      onClose={onClose}
      slot="priority-dialog"
      error={
        <FormError
          what={current ? "Saving the priority" : "Adding the priority"}
          error={save.error}
        />
      }
    >
      <Field>
        <FieldLabel htmlFor={`${id}-title`}>Priority</FieldLabel>
        <Input
          id={`${id}-title`}
          value={title}
          autoFocus
          onChange={(e) => setTitle(e.target.value)}
        />
      </Field>
      <Field>
        <FieldLabel htmlFor={`${id}-milestones`}>Milestones, one per line</FieldLabel>
        <Textarea
          id={`${id}-milestones`}
          rows={3}
          value={milestones}
          onChange={(e) => setMilestones(e.target.value)}
        />
        <FieldDescription>Optional.</FieldDescription>
      </Field>
    </QuickAddDialog>
  );
}

/**
 * SEAT-DRAWER-CLICKS: a priority read by someone who may not change it (a click on its row in
 * the drawer or the Priorities modal): its track and milestones, nothing to edit.
 */
export function PriorityDetailModal({
  goal,
  quarter,
  onClose,
}: {
  goal: Goal;
  quarter: string | null;
  onClose: () => void;
}) {
  const done = goal.milestones.filter((m) => m.done_at !== null).length;
  return (
    <SectionModal
      title={goal.title}
      description={quarter ? `Priority · ${quarter}` : "Priority"}
      onClose={onClose}
      slot="priority-detail"
      size="default"
    >
      <p className="flex items-center gap-2 text-sm text-secondary" data-slot="priority-track">
        <LensBandDot band={TRACK_BAND[goal.track]} />
        {TRACK_LABELS[goal.track]}
      </p>
      {goal.milestones.length ? (
        <ul className="flex flex-col gap-1 text-sm" data-slot="priority-milestones">
          {goal.milestones.map((m) => (
            <li key={m.id} className="flex min-w-0 items-center gap-2">
              {m.done_at ? (
                <CheckIcon aria-label="Done" className="size-4 shrink-0 text-success-text" />
              ) : (
                <CircleIcon aria-label="Not done" className="size-4 shrink-0 text-faint" />
              )}
              <span
                className={cn(
                  "min-w-0 flex-1 truncate",
                  m.done_at ? "text-faint line-through" : "text-secondary",
                )}
              >
                {m.title}
              </span>
            </li>
          ))}
          <li className="text-xs text-faint">
            {done} of {goal.milestones.length} milestones done
          </li>
        </ul>
      ) : (
        <p className="text-xs text-faint">No milestones.</p>
      )}
    </SectionModal>
  );
}
